import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BarChartIcon from "@mui/icons-material/BarChart";
import BoltIcon from "@mui/icons-material/Bolt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import { apiRequest } from "../api/apiClient";
import { validateEmailField } from "../utils/validation";

const features = [
  {
    icon: <TrendingUpIcon fontSize="small" />,
    text: "30% Increase in Machine Utilization",
  },
  {
    icon: <AccessTimeIcon fontSize="small" />,
    text: "40% Reduction in Downtime",
  },
  {
    icon: <BoltIcon fontSize="small" />,
    text: "2X Faster Production Visibility",
  },
  {
    icon: <BarChartIcon fontSize="small" />,
    text: "1.5X Better Decision Making with OEE Insights",
  },
];

const sources = [
  "Google Search",
  "LinkedIn",
  "Referral",
  "Advertisement",
  "Event / Webinar",
  "Email Campaign",
  "Other",
];

const industries = ["Automotive", "Pharma", "FMCG", "Manufacturing", "Other"];
const timelines = ["Immediate", "1-3 Months", "3-6 Months", "Just Exploring"];

type FormType = {
  name: string;
  email: string;
  phone: string;
  company: string;
  machineType: string;
  source: string;
  industry: string;
  machines: string;
  timeline: string;
};

type ErrorType = Partial<Record<keyof FormType | "api", string>>;

export default function BookDemoPage() {
  const [form, setForm] = useState<FormType>({
    name: "",
    email: "",
    phone: "",
    company: "",
    machineType: "",
    source: "",
    industry: "",
    machines: "",
    timeline: "",
  });

  const [errors, setErrors] = useState<ErrorType>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const validatePhone = (value: string) => {
    try {
      const phoneNumber = parsePhoneNumberFromString("+" + value);
      if (!phoneNumber || !phoneNumber.isValid()) {
        return "Invalid phone number";
      }
      return "";
    } catch {
      return "Invalid phone number";
    }
  };

  const validators: Partial<Record<keyof FormType, (value: string) => string>> =
    {
      email: validateEmailField,
      phone: validatePhone,
    };

  const validateField = (field: keyof FormType, value: string): string => {
    const validator = validators[field];
    if (!validator) return "";
    return validator(value);
  };

  const handleChange = (field: keyof FormType, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    const error = validateField(field, value);

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateForm = () => {
    const newErrors: ErrorType = {};

    if (!form.name) newErrors.name = "Name is required";
    if (!form.email) newErrors.email = "Email is required";
    if (!form.phone) newErrors.phone = "Phone is required";
    if (!form.company) newErrors.company = "Company is required";

    const emailError = validateEmailField(form.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(form.phone);
    if (phoneError) newErrors.phone = phoneError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = useMemo(() => {
    return (
      form.name &&
      form.company &&
      !validateEmailField(form.email) &&
      !validatePhone(form.phone)
    );
  }, [form]);

  const handleSubmit = async () => {
    setSuccess("");

    if (!validateForm()) return;

    try {
      setLoading(true);

      const phoneNumber = parsePhoneNumberFromString("+" + form.phone);

      await apiRequest("/bookademo", {
        method: "POST",
        body: {
          ...form,
          phone: phoneNumber?.format("E.164") || form.phone,
        },
      });

      setSuccess("Demo request submitted successfully");

      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        machineType: "",
        source: "",
        industry: "",
        machines: "",
        timeline: "",
      });
    } catch (err: any) {
      if (err?.status === 409) {
        setErrors({
          api: "We have already received your request. Our team will contact you shortly.",
        });
      } else {
        setErrors({
          api: err.message || "Something went wrong",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 15 }}>
        <Grid container spacing={6} alignItems="center">
          {/* LEFT */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography sx={{ fontSize: "34px", fontWeight: 700 }}>
              Book a Free Demo
            </Typography>

            <Typography sx={{ fontSize: "14.5px", color: "#6b7280", mb: 4 }}>
              See how our OEE platform improves machine efficiency.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Full Name*"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Email*"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>

              {/* ✅ PHONE WITH COUNTRY CODE */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <PhoneInput
                  country={"in"}
                  value={form.phone}
                  onChange={(value) => handleChange("phone", value)}
                  inputStyle={{ width: "100%", height: "40px" }}
                />
                {errors.phone && (
                  <Typography ml={1.8} color="error" fontSize={12}>
                    {errors.phone}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Company Name*"
                  value={form.company}
                  onChange={(e) => handleChange("company", e.target.value)}
                  error={!!errors.company}
                  helperText={errors.company}
                />
              </Grid>

              {/* ✅ NEW FIELDS */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Industry"
                  value={form.industry}
                  onChange={(e) => handleChange("industry", e.target.value)}
                >
                  {industries.map((i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Number of Machines"
                  value={form.machines}
                  onChange={(e) => handleChange("machines", e.target.value)}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Timeline"
                  value={form.timeline}
                  onChange={(e) => handleChange("timeline", e.target.value)}
                >
                  {timelines.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Machine Type"
                  value={form.machineType}
                  onChange={(e) => handleChange("machineType", e.target.value)}
                >
                  <MenuItem value="cnc">CNC Machines</MenuItem>
                  <MenuItem value="injection">Injection</MenuItem>
                  <MenuItem value="press">Press</MenuItem>
                  <MenuItem value="assembly">Assembly</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Source"
                  value={form.source}
                  onChange={(e) => handleChange("source", e.target.value)}
                >
                  {sources.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              sx={{ mt: 3, bgcolor: "#FFC400", color: "black" }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
            {/* ✅ ADD HERE */}
            {success && (
              <Typography color="green" sx={{ mt: 2 }}>
                {success}
              </Typography>
            )}

            {errors.api && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errors.api}
              </Typography>
            )}
          </Grid>

          {/* RIGHT SIDE unchanged */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: "14px",
                bgcolor: "#eef2f7",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15.5px",
                  fontWeight: 700,
                  color: "#111827",
                  mb: 3,
                  lineHeight: 1.4,
                }}
              >
                Unlock higher productivity and real-time visibility with smart
                OEE monitoring.
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                {features.map((item, i) => (
                  <Box key={i} display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
                        color: "#2563eb",
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* IMAGE */}
              <Box mt={15}>
                <img
                  src="/oee-dashboard.png"
                  alt="dashboard"
                  style={{
                    width: "60%",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
